import {
	COLORS,
	MaterialIcons,
	TertiaryButton,
} from '@monthio/react-components'
import { ErrorBoundary } from '@sentry/react'
import React, { FunctionComponent, useState } from 'react'
import styled from 'styled-components'
import { LocalOverrides, LocalOverridesDot } from './LocalOverrides'
import { Loggers } from './Loggers'
import { FlowContext } from './FlowContext'
import { DevToolsPortal } from './DevToolsPortal'
import { OverrideAppModal } from './LocalOverrides/OverrideAppModal'

const DevToolsContainer = styled.div`
	position: fixed;
	bottom: 0;
	right: 0;
	z-index: 9000;
`

const DevToolsOverlay = styled.div`
	position: relative;
	pointer-events: none;
`

const ContentMinimized = styled.div`
	display: flex;
	flex-flow: row nowrap;
	background-color: ${COLORS.BACKGROUND_DARK};
	pointer-events: all;
	border: 1px solid ${COLORS.GREY_SCALE.LIGHT_GREY};
	border-radius: 8px;
	box-shadow: 0px 0px 14px rgba(53, 64, 82, 0.05);
`

const Content = styled(ContentMinimized)`
	flex-flow: column nowrap;
	max-width: 600px;
	max-height: 300px;
	width: 600px;
	height: 300px;
`

const Tabs = styled.div`
	display: flex;
	flex-flow: row nowrap;
`

const Tab = styled.div<{ $active: boolean }>`
	display: ${(props) => (props.$active ? 'flex' : 'none')};
	background-color: ${COLORS.GREY_SCALE.WHITE};
	width: 100%;
	height: 100%;
	padding: 8px;
	overflow: scroll;
`

const Button = styled(TertiaryButton)<{ $active: boolean }>`
	display: ${(props) => (props.hidden ? 'none' : 'flex')};
	${(props) =>
		props.$active
			? `background-color: ${COLORS.GREY_SCALE.DARK_DETAIL_GREY}`
			: null};
	margin: 4px;
	> svg {
		padding-right: 0;
	}
`

const saveDevtoolsStateToStorage = (
	showDevTools: boolean,
	minimized = false,
) => {
	if (typeof localStorage !== 'undefined') {
		const state = JSON.stringify({ showDevTools, minimized })
		localStorage.setItem('devtools', state)
	}
}

const getDevToolsStateFromStorage = () => {
	if (typeof localStorage !== 'undefined') {
		const state = localStorage.getItem('devtools')
		if (state) {
			const { showDevTools, minimized } = JSON.parse(state)
			return { showDevTools, minimized }
		}
	}
	return { showDevTools: false, minimized: false }
}

const getDevtoolsStateFromUrl = () => {
	const search = location.search
	let shouldShow = false
	let minimized = false

	const localStorage = getDevToolsStateFromStorage()
	shouldShow = localStorage.showDevTools
	minimized = localStorage.minimized

	if (search) {
		const params = new URLSearchParams(search)
		params.forEach((value, name) => {
			if (name.toLowerCase() === 'devtools') {
				shouldShow = value.toLowerCase() === 'true'
				saveDevtoolsStateToStorage(shouldShow, minimized)
			}
		})
	}

	return { showDevTools: shouldShow, minimized }
}

export const DevTools: FunctionComponent<{ children?: never }> = () => {
	const [showDevTools, setShowDevTools] = useState(
		getDevtoolsStateFromUrl().showDevTools,
	)
	const [activeTab, setActiveTab] = useState('localOverride')
	const [minimized, setMinimized] = useState(
		getDevtoolsStateFromUrl().minimized || false,
	)

	const toggleMinimize = () => {
		setMinimized((prev) => {
			saveDevtoolsStateToStorage(showDevTools, !prev)
			return !prev
		})
	}
	const selectTab = (tab: string) => () => {
		setActiveTab(tab)
		if (minimized) {
			toggleMinimize()
		}
	}

	return (
		<DevToolsPortal>
			<DevToolsOverlay>
				{showDevTools ? <LocalOverridesDot /> : null}
				{showDevTools ? (
					<DevToolsContainer>
						{minimized ? (
							<ContentMinimized>
								<OverrideAppModal />
								<Button
									$active={false}
									tooltip={minimized ? 'Expand devtools' : 'Minimize devtools'}
									tooltipPlacement="top"
									onClick={toggleMinimize}
								>
									{minimized ? (
										<MaterialIcons.ArrowDropUp />
									) : (
										<MaterialIcons.ArrowDropDown />
									)}
								</Button>
								<Button
									$active={false}
									tooltip={'Close devtools'}
									tooltipPlacement="top"
									onClick={() => {
										setShowDevTools(false)
										saveDevtoolsStateToStorage(false, minimized)
									}}
								>
									<MaterialIcons.Cancel />
								</Button>
							</ContentMinimized>
						) : (
							<Content>
								<Tabs>
									<OverrideAppModal />

									<Button
										$active={activeTab === 'logger'}
										onClick={selectTab('logger')}
									>
										Logger
									</Button>
									<Button
										$active={activeTab === 'localOverride'}
										onClick={selectTab('localOverride')}
									>
										Local override
									</Button>
									<Button
										$active={activeTab === 'flowContext'}
										onClick={selectTab('flowContext')}
									>
										Portal context
									</Button>
									<Button
										$active={false}
										tooltip={
											minimized ? 'Expand devtools' : 'Minimize devtools'
										}
										tooltipPlacement="top"
										onClick={toggleMinimize}
									>
										{minimized ? (
											<MaterialIcons.ArrowDropUp />
										) : (
											<MaterialIcons.ArrowDropDown />
										)}
									</Button>
									<Button
										$active={false}
										tooltip={'Close devtools'}
										tooltipPlacement="top"
										onClick={() => {
											setShowDevTools(false)
											saveDevtoolsStateToStorage(false, minimized)
										}}
									>
										<MaterialIcons.Cancel />
									</Button>
								</Tabs>
								<Tab $active={activeTab === 'logger'}>
									<ErrorBoundary>
										<Loggers />
									</ErrorBoundary>
								</Tab>
								<Tab $active={activeTab === 'localOverride'}>
									<ErrorBoundary>
										<LocalOverrides />
									</ErrorBoundary>
								</Tab>
								<Tab $active={activeTab === 'flowContext'}>
									<ErrorBoundary>
										<FlowContext />
									</ErrorBoundary>
								</Tab>
							</Content>
						)}
					</DevToolsContainer>
				) : null}
			</DevToolsOverlay>
		</DevToolsPortal>
	)
}
